<script lang="ts">
	import { horizontalSlide } from '$lib/utils/custom-transitions';
	import { fade, slide } from 'svelte/transition';
	import MarkdownRender from './MarkedownRender.svelte';
	import LoadingAnimation from '../../Chat/LoadingAnimation.svelte';
	import RetryIcon from '../../icons/RetryIcon.svelte';
	import { isValidUrl } from '../../../utils/utils';
	import { onMount, setContext, onDestroy } from 'svelte';
	import type { ChatObject, SourceObject } from '../../../stores/chatStore';
	import { appStore } from '../../../stores/appStore';
	import { embeddedChatStore } from '../../../stores/chatStore';
	import SourceCard from '../../Embedded/Sidebar/SourceCard.svelte'
	import EmbeddedStreamAnswerAnimation from '../../Chat/EmbeddedStreamAnswerAnimation.svelte';
	import EmbeddedStreamSourcesAnimation from '../../Chat/EmbeddedStreamSourcesAnimation.svelte';

	export let retryFunction: (sourceText: string, retryMessageId?: string) => void;
	export let message: ChatObject;
	export let questionBlockHeight: number;
	export let otherElementsHeight: number;

	let textWrapperResizeObserver: ResizeObserver;
	let questionWrapperResizeObserver: ResizeObserver;
	let hasRetried = false;
	let tabFocused: boolean = true;
	let content: string;
	let isUser: boolean;
	let defaultMessage: string | undefined;
	let hasError: boolean;
	let sourceText: string;
	let time: string;
	let id: string;
	let sources: SourceObject[];
	let defaultContent: string;
	let innerWidth: number;
	let textWrapperElement: HTMLElement;
	let textWrapperHeight: number = 0;
	let elementWrapperElement: HTMLElement;
	let animated: boolean;
	let isNewAnswer: boolean;
	let isNewSources: boolean;
	let isLastQuestion: boolean = false;
	let isLastAnswer: boolean = false;
	let wrapperStyle: string;

	$: if (!textWrapperResizeObserver && textWrapperElement) {
		textWrapperResizeObserver = new ResizeObserver((entries) => {
			// We're only watching one element
			const entry = entries.at(0);
			if (entry?.target) {
				textWrapperHeight = entry.target.scrollHeight;
			}
		});
		textWrapperResizeObserver.observe(textWrapperElement);
	}

	$: if (!questionWrapperResizeObserver) {
		questionWrapperResizeObserver = new ResizeObserver((entries) => {
			// We're only watching one element
			const entry = entries.at(0);
			if (entry?.target) {
				questionBlockHeight = entry.target.scrollHeight;
			}
		});
	}

	$: {
		content = message.message;
		isUser = message.isUser;
		defaultMessage = message.defaultMessageNumber || '';
		hasError = !!message.hasError;
		sourceText = message.sourceText || '';
		time = message.time || new Date().toString();
		id = message.id;
		sources = (message.sources || []).filter((item) => isValidUrl(item.source || item.url));
	}
	$: animated = $appStore.animatedMessageIds.includes(id);
	$: isNewAnswer = animated && !$appStore.animatedMessagesData[id]?.answerTaskIsDone;
	$: isNewSources = animated && !$appStore.animatedMessagesData[id]?.sourcesTaskIsDone;
	$: {
		if ($embeddedChatStore.messages[$embeddedChatStore.activeChatId]?.length > 2) {
			const index = $embeddedChatStore.messages[$embeddedChatStore.activeChatId].findIndex(item => item.id === id);
			isLastQuestion = index === $embeddedChatStore.messages[$embeddedChatStore.activeChatId].length - 2;
			isLastAnswer = index === $embeddedChatStore.messages[$embeddedChatStore.activeChatId].length - 1;
			wrapperStyle = `min-height: ${isLastAnswer ? `calc(100vh - ${otherElementsHeight}px)` : "none"}`
			if (isLastQuestion && elementWrapperElement) {
				questionWrapperResizeObserver.observe(elementWrapperElement);
			} else {
				questionWrapperResizeObserver?.disconnect()
			}
		} else {
			wrapperStyle = `min-height: none;`;
			questionWrapperResizeObserver?.disconnect();
		}
	}

	setContext('messageId', message.id);

	const handleVisibilityChange = () => {
		tabFocused = document.visibilityState === 'visible';
	};

	onMount(() => {
		document.addEventListener('visibilitychange', handleVisibilityChange);
	});
	onDestroy(() => {
		textWrapperResizeObserver?.disconnect();
		questionWrapperResizeObserver?.disconnect();
	});
</script>

<div
	bind:clientWidth={innerWidth}
	class="main-wrapper"
	class:right={isUser}
	style={wrapperStyle}
	bind:this={elementWrapperElement}
>
	<div
		role="presentation"
		class="bubble-wrapper"
		class:fullWidth={innerWidth > 400}
		class:right={isUser}
	>
		{#if sources.length}
			<span class="section-title" in:horizontalSlide>
				<div class="animation-wrapper">
					<EmbeddedStreamSourcesAnimation isNewMessage={isNewSources} />
				</div>
				Sources
			</span>
			<div class="source-cards-wrapper">
				{#each sources as source}
					<SourceCard
						url={source.source || source.url}
						title={source.title}
						index={sources.indexOf(source)}
					/>
				{/each}
			</div>
		{/if}
		{#if !isUser && content}
			<span class="section-title answer" in:horizontalSlide>
				<div class='left'>
					<div class="animation-wrapper">
						<EmbeddedStreamAnswerAnimation isNewMessage={isNewAnswer} />
					</div>
					Answer
				</div>
			</span>
		{/if}
		<div class="checkbox-bubble-wrapper">
			<div
				role="presentation"
				class="bubble"
				class:right={isUser}
				class:answer={!isUser && content}
				class:isLastAnswer
				in:fade|global={{ duration: 200 }}
				{id}
			>
				{#if !content && !defaultContent && tabFocused && !$appStore.animatedMessagesData[id]?.message && !sources.length}
					<LoadingAnimation />
				{:else if isUser}
					<p class="user-text">{content}</p>
				{:else}
					<div class="text-wrapper" style="height: {animated ? textWrapperHeight + 'px' : 'auto'}">
						<div style="padding: 1px 0" bind:this={textWrapperElement}>
							<MarkdownRender
								systemMessage={!!defaultContent}
								content={defaultContent || content}
								isNewMessage={isNewAnswer}
							/>
						</div>
					</div>
				{/if}
			</div>
		</div>
	</div>
	{#if hasError && !hasRetried}
		<button
			class="retry-button"
			on:click={() => {
				hasRetried = true;
				retryFunction(sourceText, id);
			}}
		>
			<div class="retry-icon-wrapper">
				<RetryIcon />
			</div>
			Retry
		</button>
	{/if}
</div>

<style lang="scss">
	.main-wrapper {
		width: 100%;
		display: flex;
		align-items: flex-start;
		flex-direction: column;
		font-weight: 300;
		&.right {
			align-items: flex-end;
		}
	}

	.bubble-wrapper {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: flex-start;
		gap: 10px;
		width: 100%;
		padding: 4px 0;
		&.right {
			flex-direction: row-reverse;
		}

		.checkbox-bubble-wrapper {
			width: 100%;
			display: flex;
			align-items: center;
			justify-content: center;
			width: 100%;
			gap: 8px;
		}

		& .section-title {
			font-size: 14px;
			color: var(--button-color);
			width: 100%;
			margin-top: 10px;
			display: flex;
			align-items: center;
			gap: 12px;

			&.answer {
				margin-top: 24px;

				.left {
					display: flex;
					align-items: center;
					justify-content: center;
					gap: 12px;
				}
			}

			& .animation-wrapper {
				height: 46px;
				width: 32px;
				overflow: hidden;
				display: flex;
				align-items: center;
				justify-content: center;
			}
		}

		@media screen and (max-width: 830px) {
			max-width: calc(100vw - 30px);
		}
	}

	.bubble {
		position: relative;
		color: var(--avi-chat-text-color);
		width: 100%;
		transition: all 0.1s linear, color 0s linear;
		line-height: 28px;
		scroll-margin-bottom: 120px;
		font-size: 22px;
		font-weight: 500;
		border-bottom: 1px solid rgba(128, 128, 128, 0.4);
		padding-bottom: 20px;

		@media screen and (max-width: 830px) {
			max-width: calc(100vw - 30px);
		}

		&.isLastAnswer {
			border-bottom: none;
		}

		&.answer {
			font-size: 14px;
			font-weight: 300;
			padding-bottom: 45px;
		}
		&.right {
			background-color: transparent;
			border: solid 1px transparent;
			transform-origin: bottom right;
			padding: 10px 0px;
			margin-top: 60px;
		}
		.user-text {
			white-space: pre-wrap;
		}
		.text-wrapper {
			font-size: 14px;
			height: 0;
			transition: all 0.2s linear, color 0s linear;
			overflow: hidden;
		}
	}
	:global(ol),
	:global(ul) {
		margin-block-start: 1em;
		margin-block-end: 1em;
		margin-inline-start: 0;
		margin-inline-end: 0;
		padding-inline-start: 40px;
	}

	.retry-button {
		margin-bottom: 10px;
		background: var(--retry-button-bg);
		border: none;
		color: white;
		cursor: pointer;
		padding: 8px 12px;
		border-radius: 0 0px 8px 8px;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 6px;

		@media (hover: hover) {
			&:hover {
				filter: brightness(1.2);
			}
		}
	}

	.retry-icon-wrapper {
		height: 20px;
		width: 20px;
		color: white;
	}
	.source-cards-wrapper {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
		width: 100%;
		max-width: 100%;
		overflow-y: scroll;
		font-size: 12px;
		&::-webkit-scrollbar {
			display: none;
		}
	}
</style>
